import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useFeatureUsage, PRODUCT_AREAS } from '../../hooks/useFeatureUsage';
import { 
  Typography, 
  Box, 
  Paper, 
  Grid, 
  Container, 
  TextField,
  InputAdornment,
  Link,
  LinearProgress
} from '@mui/material';
import { 
  AssessmentOutlined,
  MapOutlined, 
  AutoGraphOutlined, 
  MapsHomeWorkOutlined, 
  DonutSmallOutlined,
  PersonPinCircleOutlined,
  Search as SearchIcon,
  Share as ShareIcon,
  ContactSupportOutlined,
  ChevronRight
} from '@mui/icons-material';
import AssistantOutlinedIcon from '@mui/icons-material/AssistantOutlined';
import { useNavigate } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import { MapContainer, TileLayer, Marker, useMap, Popup } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import { formatSchoolName } from '../../utils/nameFormatter';
import { canAccessExplorers } from '../../utils/explorerAccess';
import 'leaflet/dist/leaflet.css';
import { useTeamSchoolCache } from '../../hooks/teamCacheService';
import { useAISummaryAvailability } from '../../contexts/AISummaryContext';
import { useDistrictCache } from '../../hooks/useDistrictCache';
import _ from 'lodash';
import PortfolioMetricsSummary from './PortfolioMetricsSummary';
import SchoolHighlights from './SchoolHighlights';


// Custom icon for map markers
const markerIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Map bounds fitting component
const MapBoundsFitter = ({ schools }) => {
  const map = useMap();
  
  useEffect(() => {
    if (schools.length > 0) {
      const bounds = L.latLngBounds(schools.map(school => [school.latitude, school.longitude]));
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [schools, map]);
  
  return null;
};

const getFeatureSections = (userRole, hasAISummaries, isAdmin) => {
  const sections = [];

  // Only show "Explore Specifics" if user has access to explorers
  if (canAccessExplorers(userRole)) {
    sections.push({
      title: 'Explore Specifics',
      features: [
        {
          title: 'Geographic Explorer',
          description: 'Understand local context with population and enrollment trends.',
          icon: <MapsHomeWorkOutlined />,
          link: '/geographic-explorer'
        },
        {
          title: 'Competitive Explorer', 
          description: 'Analyze market share for schools within a selected radius.',
          icon: <DonutSmallOutlined />,
          link: '/competitive-explorer'
        },
        {
          title: 'Projections Explorer',
          description: 'Explore historical and projected enrollment data for the next five years.',
          icon: <AutoGraphOutlined />,
          link: '/projections-explorer'
        },
        {
          title: 'Student Catchment Explorer',
          description: 'Visualize where your students live and how far they travel to school.',
          icon: <PersonPinCircleOutlined />,
          link: '/student-catchment-explorer'
        }
      ]
    });
  }

  // "Analyze Trends" section - available to all users
  const analyzeTrendsFeatures = [
    {
      title: 'School Summary',
      description: 'Get a comprehensive overview of key metrics and performance indicators.',
      icon: <AssessmentOutlined />,
      link: '/school-summary'
    },
    {
      title: 'Portfolio Analysis',
      description: 'Analyze portfolio-wide trends and patterns with detailed demographic data.',
      icon: <MapOutlined />,
      link: '/district-analysis'
    }
  ];
  
  // Add AI Summary only if user has explorer access AND has AI summaries available (or is admin)
  if (canAccessExplorers(userRole) && (hasAISummaries || isAdmin)) {
    analyzeTrendsFeatures.unshift({
      title: 'AI Summary',
      description: 'AI-powered insights combining geographic, competitive, and projections analysis.',
      icon: <AssistantOutlinedIcon />,
      link: '/ai-summary'
    });
  }
  
  sections.push({
    title: 'Analyze Trends',
    features: analyzeTrendsFeatures
  });

  // "Share Insights" section - available to all users
  sections.push({
    title: 'Share Insights',
    features: [
      {
        title: 'Resources',
        description: 'Access presentation templates, one-pagers, and documentation.',
        icon: <ShareIcon />,
        link: '/resources'
      },
      {
        title: 'Support',
        description: 'Get help, schedule tutorials, and access video guides.',
        icon: <ContactSupportOutlined />,
        link: '/support'
      },
      {
        // Blank placeholder to maintain alignment with other columns
        title: null,
        description: null,
        icon: null,
        link: null,
        isPlaceholder: true
      }
    ]
  });

  return sections;
};

// Add this CSS to your styles
const mapStyles = `
  .custom-popup .leaflet-popup-content-wrapper {
    background: white;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    border: 2px solid #ff4444;
  }
  .custom-popup .leaflet-popup-tip-container {
    display: none;
  }
  .custom-popup .leaflet-popup-content {
    margin: 8px;
    width: auto !important;
  }
`;

// ErrorState component
const ErrorState = () => (
  <Container maxWidth="md">
    <Paper 
      elevation={3} 
      sx={{ 
        p: 4, 
        mt: 4,
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        background: 'linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)',
        borderRadius: 2
      }}
    >
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <MapsHomeWorkOutlined 
          sx={{ 
            fontSize: 64, 
            color: 'primary.main',
            mb: 2,
            opacity: 0.8
          }} 
        />
        <Typography variant="h5" color="primary" gutterBottom>
          No Schools Available
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          You currently have no schools assigned to your account.
          This might be due to maintenance or upgrades.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Try back again later or contact{' '}
          <Link 
            href="mailto:support@educationdatacollective.com"
            sx={{ 
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            support@educationdatacollective.com
          </Link>
        </Typography>
      </Box>
    </Paper>
  </Container>
);

const Home = ({ user }) => {
  useFeatureUsage(PRODUCT_AREAS.SCHOOL_EXPLORER);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = mapStyles;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);
  const displayName = user?.displayName || user?.email || 'Guest';
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const [totalSchoolCount, setTotalSchoolCount] = useState(0);

  // Get AI summary availability
  const { hasAISummaries, isAdmin } = useAISummaryAvailability();

  // Implement cache hooks with proper error boundaries
  const { 
    schools, 
    loading: schoolsLoading, 
    error: schoolsError,
    refreshTeamSchools,
    loadingStatus,
    isBackgroundLoading,
    totalSchoolsCount
  } = useTeamSchoolCache();
  
  // Get district data to check if we should show highlights
  const { districtData } = useDistrictCache({ silentLoad: true });
  
  // Determine if highlights section should be shown
  const shouldShowHighlights = useMemo(() => {
    if (!districtData?.metrics) return false;
    const validSchools = districtData.metrics.filter(
      school => school?.population && school?.enrollment && school?.market_share
    );
    // Don't show if less than 5 schools (same logic as SchoolHighlights component)
    return validSchools.length >= 5;
  }, [districtData]);

  useEffect(() => {
    // Leave the loading screen once the schools request finishes — including
    // the empty/error case. Requiring schools.length > 0 left this spinner
    // up forever when the API returned no schools.
    if (!schoolsLoading && isInitialLoad) {
      const timer = setTimeout(() => {
        setIsInitialLoad(false);
      }, 200);
      return () => clearTimeout(timer);
    }

    // Keep the progress animation only during initial loading
    if (schoolsLoading && isInitialLoad) {
      const progressInterval = setInterval(() => {
        setLoadingProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 500);
  
      return () => clearInterval(progressInterval);
    }
  }, [schoolsLoading, isInitialLoad]);

  // Clean up search results on unmount
  useEffect(() => {
    return () => {
      if (searchResults.length > 0) {
        setSearchResults([]);
      }
    };
  }, [searchResults]);

  // Fix the map cleanup effect
  useEffect(() => {
    const currentMapRef = mapRef.current;
    return () => {
      if (currentMapRef) {
        currentMapRef.remove();
      }
    };
  }, []);
  
  // Add refresh functionality if needed:
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      refreshTeamSchools();
    }, 300000); // Refresh every 5 minutes
  
    return () => clearInterval(refreshInterval);
  }, [refreshTeamSchools]);

  /// Filter schools directly (same pattern as InitialSchoolSelector)
const handleSearch = useCallback((term) => {
  if (!term?.trim()) {
    setSearchResults([]);
    return;
  }
  
  setSearching(true);
  
  // Simple case-insensitive search against name, city, state
  const query = term.toLowerCase().trim();
  const results = schools.filter(school => {
    // Add null checks to prevent "Cannot read properties of null" errors
    const name = school.name || '';
    const city = school.city || '';
    const state = school.state || '';
    const ncessch = school.ncessch || '';
    
    return name.toLowerCase().includes(query) ||
           city.toLowerCase().includes(query) ||
           state.toLowerCase().includes(query) ||
           ncessch.includes(query);
  }).slice(0, 15); // Limit to 15 results for performance
  
  setSearchResults(results);
  setSearching(false);
}, [schools]);

useEffect(() => {
  if (isBackgroundLoading) {
    // This assumes you've added a way to export the totalSchoolsRef.current 
    // value from your teamCacheService hook
    setTotalSchoolCount(totalSchoolsCount || schools.length * 2);
  }
}, [isBackgroundLoading, schools.length, totalSchoolsCount]);

// Debounce the search
useEffect(() => {
  const debouncedSearch = _.debounce(handleSearch, 300);
  debouncedSearch(searchTerm);
  return () => debouncedSearch.cancel();
}, [searchTerm, handleSearch]);

  // Progressive loading with cache awareness
  useEffect(() => {
    if (!schoolsLoading && schools.length > 0) {
      // Delay map rendering to prioritize critical UI
      const timer = setTimeout(() => setShowMap(true), 100);
      return () => clearTimeout(timer);
    }
  }, [schoolsLoading, schools]);

  // Calculate unique states and cities
  const metrics = useMemo(() => {
    if (schoolsLoading || !schools.length) return { stateCount: 0, cityCount: 0 };
    
    const uniqueStates = new Set(schools.map(school => school.state));
    const uniqueCities = new Set(schools.map(school => `${school.city}, ${school.state}`));
    
    return {
      stateCount: uniqueStates.size,
      cityCount: uniqueCities.size
    };
  }, [schools, schoolsLoading]);

  const handleSchoolClick = useCallback((school) => {
    // Use query parameters instead of path parameters
    navigate(`/school-summary?school=${school.uuid}`);
  }, [navigate]);

  const groupedSchools = useMemo(() => {
    if (!schools.length) return {};
    
    const grouped = {};
    schools.forEach(school => {
      const key = `${school.latitude},${school.longitude}`;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(school);
    });
    return grouped;
  }, [schools]);
 
  if (isInitialLoad) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            gap: 2
          }}
        >
          <Typography variant="h5" color="primary" gutterBottom>
            Loading Your Schools
          </Typography>
          <CircularProgress 
            variant="determinate" 
            value={loadingProgress} 
            size={60}
          />
          <Typography variant="body2" color="text.secondary">
            {loadingProgress < 100 
              ? loadingStatus || "Loading school data..." 
              : "Almost there..."}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            This may take a moment if you have many schools
          </Typography>
        </Paper>
      </Container>
    );
  }
  
  // Only show error if it's not the initial load
  if (schoolsError && !isInitialLoad) {
    return <ErrorState />;
  }

   return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      {/* Background loading indicator */}
      {isBackgroundLoading && (
        <LinearProgress 
          variant="indeterminate" 
          sx={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            height: 2, 
            zIndex: 9999,
            opacity: 0.7
          }} 
        />
      )}
      <Grid container spacing={2}>
        {/* Row 1: Welcome Section - Full Width */}
        <Grid item xs={12}>
          <Paper 
            elevation={3} 
            sx={{ 
              p: 2.5, 
              background: 'linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)', 
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 3 }}>
              {/* Welcome text */}
              <Box sx={{ flex: 1, minWidth: '300px' }}>
                <Typography 
                  variant="overline" 
                  component="div"
                  sx={{ 
                    fontWeight: 600,
                    color: 'primary.main',
                    fontSize: '0.75rem',
                    letterSpacing: '0.1em',
                    lineHeight: 1,
                    mb: 0.5,
                  }}
                >
                  SCHOOL EXPLORER 2.0
                </Typography>
                <Typography 
                  variant="h6" 
                  component="div"
                  sx={{ 
                    fontWeight: 500,
                    color: 'text.secondary',
                    fontSize: '1rem',
                    lineHeight: 1.2,
                  }}
                >
                  Welcome back,
                </Typography>
                <Typography 
                  variant="h4" 
                  component="div"
                  sx={{ 
                    fontWeight: 700,
                    color: 'primary.main',
                    fontSize: '1.75rem',
                    lineHeight: 1.2,
                    mt: 0.25,
                  }}
                >
                  {displayName}
                </Typography>
              </Box>
              
              {/* Search Box - Center */}
              <Box sx={{ flex: 2, minWidth: '300px', maxWidth: '500px' }}>
                {isBackgroundLoading && totalSchoolCount > 0 && (
                  <Box sx={{ mb: 1, p: 0.75, bgcolor: 'info.lightest', borderRadius: 1 }}>
                    <Typography variant="caption" color="info.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CircularProgress size={12} color="info" />
                      Loading: {schools.length}/{totalSchoolCount}
                    </Typography>
                  </Box>
                )}
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search schools..."
                  size="medium"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                <Box sx={{
                  maxHeight: '120px',
                  overflowY: 'auto',
                  border: searchTerm ? '1px solid' : 'none',
                  borderColor: 'divider',
                  borderRadius: 1,
                  mt: 0.5,
                  bgcolor: 'background.paper',
                }}>
                  {searching ? (
                    <Box sx={{ p: 1, textAlign: 'center' }}>
                      <CircularProgress size={16} />
                    </Box>
                  ) : (
                    <>
                      {searchResults.map((school) => (
                        <Box
                          key={school.uuid || school.ncessch}
                          onClick={() => handleSchoolClick(school)}
                          sx={{
                            p: 1,
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                            '&:not(:last-child)': { borderBottom: '1px solid', borderColor: 'divider' }
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formatSchoolName(school.name)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {school.city}, {school.state}
                          </Typography>
                        </Box>
                      ))}
                      {searchTerm && searchResults.length === 0 && (
                        <Box sx={{ p: 1, textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary">
                            No schools found
                          </Typography>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </Box>
              
              {/* Quick Stats - Right */}
              <Box sx={{ display: 'flex', gap: 4, minWidth: '200px' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" color="primary" sx={{ fontWeight: 700, lineHeight: 1 }}>
                    {schools.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mt: 0.5 }}>
                    Schools
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" color="primary" sx={{ fontWeight: 700, lineHeight: 1 }}>
                    {metrics.cityCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mt: 0.5 }}>
                    Cities
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Row 2: Map + Bright Spots + Schools to Investigate (responsive width) */}
        <Grid item xs={12} md={shouldShowHighlights ? 4 : 12}>
          {/* Map Section */}
          <Paper 
            elevation={3} 
            sx={{ 
              height: '280px',
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            {showMap ? (
              <MapContainer style={{ height: "100%", width: "100%" }}>
                <TileLayer
                  url="https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.png"
                  attribution='&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>'
                />
                <MapBoundsFitter schools={schools} />
                <MarkerClusterGroup
                  chunkedLoading
                  spiderfyOnMaxZoom={true}
                  zoomToBoundsOnClick={true}
                  maxClusterRadius={40}
                  disableClusteringAtZoom={12}
                >
                  {Object.entries(groupedSchools).map(([key, schoolsAtLocation], index) => {
                    const position = [schoolsAtLocation[0].latitude, schoolsAtLocation[0].longitude];
                    return (
                      <Marker 
                        key={index}
                        position={position}
                        icon={markerIcon}
                      >
                        <Popup
                          closeButton={false}
                          autoPan={false}
                          className="custom-popup"
                        >
                          <div style={{ padding: '2px', width: '150px' }}>
                            {schoolsAtLocation.map((school, idx) => (
                              <div key={idx}>
                                <div
                                  onClick={() => handleSchoolClick(school)}
                                  style={{
                                    cursor: 'pointer',
                                    padding: '3px 4px',
                                    fontSize: '13px',
                                    color: '#2196F3',
                                    wordWrap: 'break-word',
                                    lineHeight: 1.2,
                                    margin: '2px 0',
                                    textAlign: 'center',
                                    borderRadius: '3px',
                                  }}
                                  onMouseOver={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f0f7ff';
                                    e.currentTarget.style.color = '#1565c0';
                                  }}
                                  onMouseOut={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = '#2196F3';
                                  }}
                                >
                                  {formatSchoolName(school.name)}
                                </div>
                                {idx < schoolsAtLocation.length - 1 && (
                                  <div style={{ height: '1px', background: '#e0e0e0', margin: '2px 10px' }} />
                                )}
                              </div>
                            ))}
                          </div>
                        </Popup>
                      </Marker>
                    );
                  })}
                </MarkerClusterGroup>
              </MapContainer>
            ) : (
              <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress />
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Bright Spots and Schools to Investigate - Only show if team has enough schools */}
        {shouldShowHighlights && (
          <Grid item xs={12} md={8}>
            <Box sx={{ height: '280px' }}>
              <SchoolHighlights />
            </Box>
          </Grid>
        )}

        {/* Row 3: Portfolio Metrics */}
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderLeft: '4px solid',
              borderLeftColor: 'primary.main',
              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.paper' : 'background.default',
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              gap: 4, 
              alignItems: 'center',
              flexDirection: { xs: 'column', md: 'row' },
            }}>
              {/* Left side: Text */}
              <Box sx={{ 
                minWidth: { xs: '100%', md: 280 },
                maxWidth: { xs: '100%', md: 320 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <AssessmentOutlined sx={{ color: 'primary.main', fontSize: '1.5rem' }} />
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      color: 'text.primary',
                      fontWeight: 600,
                    }}
                  >
                    Portfolio Metrics
                  </Typography>
                </Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'text.secondary',
                    lineHeight: 1.6,
                    mb: 1.5,
                  }}
                >
                  Summary of your portfolio of schools. Figures refer to the percentage of schools experiencing each dynamic.
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ChevronRight sx={{ color: 'primary.main', fontSize: '1rem' }} />
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: 'primary.main',
                      fontWeight: 500,
                      fontStyle: 'italic',
                    }}
                  >
                    Click on a metric to learn more
                  </Typography>
                </Box>
              </Box>
              
              {/* Right side: Metrics */}
              <Box sx={{ flex: 1, width: '100%' }}>
                <PortfolioMetricsSummary />
              </Box>
            </Box>
          </Paper>
        </Grid>


        {/* Tools Section - Three categories with descriptions */}
        <Grid item xs={12}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 2, 
              borderRadius: 2, 
              border: '1px solid', 
              borderColor: 'divider',
              bgcolor: 'background.paper'
            }}
          >
            <Grid container spacing={3}>
              {getFeatureSections(user?.role, hasAISummaries, isAdmin).map((section, sectionIndex) => (
                <Grid item xs={12} md={4} key={sectionIndex} sx={{ display: 'flex' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    {/* Section Header */}
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        fontWeight: 700, 
                        color: 'primary.main',
                        mb: 0.5,
                        fontSize: '1.1rem'
                      }}
                    >
                      {section.title}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'text.secondary',
                        display: 'block',
                        mb: 2,
                        lineHeight: 1.5,
                        fontSize: '0.85rem'
                      }}
                    >
                      {section.title === 'Explore Specifics' && 'Dig deep into population and competitive dynamics impacting a single school.'}
                      {section.title === 'Analyze Trends' && 'Understand how shifts impact schools or portfolio, and identify opportunities.'}
                      {section.title === 'Share Insights' && 'Access templates and resources for communicating with stakeholders along with support for using School Explorer.'}
                    </Typography>
                    
                    {/* Feature Links */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1 }}>
                      {section.features.map((feature, featureIndex) => (
                        feature.isPlaceholder ? (
                          // Invisible placeholder box to maintain grid alignment
                          <Box
                            key={featureIndex}
                            sx={{
                              height: '92px',
                              visibility: 'hidden'
                            }}
                          />
                        ) : (
                          <Box
                            key={featureIndex}
                            onClick={() => navigate(feature.link)}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              py: 1.25,
                              px: 1.75,
                              borderRadius: 2,
                              cursor: 'pointer',
                              bgcolor: 'action.hover',
                              border: '1px solid',
                              borderColor: 'divider',
                              transition: 'all 0.2s ease-in-out',
                              height: '92px',
                              '&:hover': {
                                bgcolor: 'primary.lighter',
                                borderColor: 'primary.light',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                transform: 'translateY(-2px)',
                                '& .feature-icon': { 
                                  color: 'primary.main',
                                  transform: 'scale(1.1)'
                                },
                                '& .feature-title': { 
                                  color: 'primary.main' 
                                },
                                '& .chevron-icon': {
                                  transform: 'translateX(4px)',
                                  color: 'primary.main'
                                }
                              }
                            }}
                          >
                            <Box sx={{ 
                              '& .MuiSvgIcon-root': { 
                                fontSize: '1.75rem', 
                                color: 'primary.main',
                                transition: 'all 0.2s',
                                opacity: 0.9
                              } 
                            }}>
                              {React.cloneElement(feature.icon, { className: 'feature-icon' })}
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography 
                                className="feature-title"
                                variant="body2" 
                                sx={{ 
                                  fontWeight: 600, 
                                  fontSize: '0.95rem', 
                                  transition: 'color 0.2s',
                                  mb: 0.25
                                }}
                              >
                                {feature.title}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: 'text.secondary', 
                                  fontSize: '0.8rem', 
                                  lineHeight: 1.4 
                                }}
                              >
                                {feature.description}
                              </Typography>
                            </Box>
                            <ChevronRight 
                              className="chevron-icon"
                              sx={{ 
                                fontSize: '1.5rem', 
                                color: 'text.secondary',
                                transition: 'all 0.2s',
                                opacity: 0.6
                              }} 
                            />
                          </Box>
                        )
                      ))}
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Home;